import React, { useMemo, useState } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import PlaceIcon from '@mui/icons-material/Place';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

import { useGetClubsQuery } from '../api/apiSlice';
import { ClubListItem } from '../types/api';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

function formatLocation(club: ClubListItem): string {
    return [club.CITY, club.COUNTRY].filter(Boolean).join(', ');
}

interface ClubPickerProps {
    onSelect: (club: ClubListItem) => void;
    // 'list' is the compact view used in the "which club?" dialog shown to a
    // brand-new social identity; 'cards' is the fuller directory view (logo,
    // name, city, brief) used on the dedicated find-a-club page.
    variant?: 'list' | 'cards';
}

export default function ClubPicker({ onSelect, variant = 'list' }: ClubPickerProps) {
    const { t } = useTranslation();
    const [search, setSearch] = useState('');
    const { data: clubs = [], isLoading } = useGetClubsQuery();

    const filtered = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return clubs;
        return clubs.filter(
            (club) =>
                club.NAME.toLowerCase().includes(query) ||
                (club.CITY ?? '').toLowerCase().includes(query) ||
                (club.COUNTRY ?? '').toLowerCase().includes(query)
        );
    }, [clubs, search]);

    return (
        <Box>
            <TextField
                fullWidth
                placeholder={t('clubs.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 2 }}
            />

            {isLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : filtered.length === 0 ? (
                <Typography color="text.secondary" align="center" py={4}>
                    {t('clubs.noResults')}
                </Typography>
            ) : variant === 'cards' ? (
                <Grid container spacing={2} alignItems="stretch">
                    {filtered.map((club) => (
                        <Grid item key={club.ID} xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                            <Card elevation={3} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <CardActionArea
                                    onClick={() => onSelect(club)}
                                    sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={club.LOGO || PLACEHOLDER_IMAGE}
                                        alt={club.NAME}
                                        onError={handleImageError}
                                        sx={{ objectFit: 'contain', bgcolor: 'grey.100', p: 2, flexShrink: 0 }}
                                    />
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {club.NAME}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={0.5} color="text.secondary" minHeight={28} mb={0.5}>
                                            {formatLocation(club) && (
                                                <>
                                                    <PlaceIcon fontSize="small" />
                                                    <Typography variant="body2">{formatLocation(club)}</Typography>
                                                </>
                                            )}
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                minHeight: '4.5em',
                                            }}
                                        >
                                            {club.BRIEF}
                                        </Typography>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <List>
                    {filtered.map((club) => (
                        <ListItemButton key={club.ID} onClick={() => onSelect(club)} divider>
                            <ListItemAvatar>
                                <Avatar
                                    src={club.LOGO || PLACEHOLDER_IMAGE}
                                    onError={handleImageError}
                                    alt={club.NAME}
                                    variant="rounded"
                                />
                            </ListItemAvatar>
                            <ListItemText primary={club.NAME} secondary={formatLocation(club) || undefined} />
                        </ListItemButton>
                    ))}
                </List>
            )}
        </Box>
    );
}
